// @ts-ignore

import { DatePipe } from '@angular/common';

/**
 * Permet de filtrer un tableau pour avoir uniquement des valeurs distinctes sur une propriété de l'objet
 * @param array tableau d'objets à filtrer
 * @param getProperty sélecteur de la propriété utilisée pour le filtre
 * @returns tableau d'objets uniques
 */
export function distinct(array: any[], getProperty?: (arrayItem: any) => any): any[] {
  return [...new Map(array.map(item => [getProperty ? getProperty(item) : item, item])).values()];
}

export function sortBy(array: any[], getProperty?: (arrayItem: any) => any) {
  return array.sort((a, b) => {
    const propA = getProperty ? getProperty(a) : a;
    const propB = getProperty ? getProperty(b) : b;
    return propA > propB ? 1 : propA === propB ? 0 : -1;
  });
}

/**
 * Permet de cloner un objet sans garder de référence
 * @param obj
 */
export function cloneDeep<T>(obj: T): T {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  if (obj instanceof Date) {
    return new Date(obj.getTime()) as any;
  }
  if (obj instanceof Array) {
    const clonedArray = obj.map(item => cloneDeep(item));
    return clonedArray as any;
  }
  if (obj instanceof Object) {
    const clonedObj = Object.create(Object.getPrototypeOf(obj));
    for (const prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        clonedObj[prop] = cloneDeep(obj[prop]);
      }
    }
    return clonedObj as any;
  }
  throw new Error('Unable to clone object. Unsupported type.');
}

export function dateToString(date: Date): string {
  if (!date) return '';
  let dateFormat: string = 'dd/MM/yyyy';
  let datePipe = new DatePipe('fr-FR');
  return datePipe.transform(date, dateFormat);
}

export function stringToDate(dateStr: string): Date {
  if (!dateStr) return null;

  const [day, month, year] = dateStr.split('/').map(Number);

  if (!day || !month || !year) return null;

  // Les mois commencent à 0 en JavaScript (0 = janvier)
  return new Date(year, month - 1, day);
}

export function isObjectEmpty(obj: any): boolean {
  if (!obj) return true;
  return Object.keys(obj).length === 0;
}

export function isGuidValue(value: string | null | undefined): value is string {
  return !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export function getLastDayOfMonth(date) {
  if (!date || !(date instanceof Date)) {
    return null;
  }

  const year = date.getFullYear();
  const month = date.getMonth();

  // Le jour 0 du mois suivant = dernier jour du mois courant
  return new Date(year, month + 1, 0, 23, 59);
}

export function dateEquals(date1: Date, date2: Date) {
  if (!date1 || !date2) return false;
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) return false;

  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

export function timeEquals(date1: Date, date2: Date) {
  if (!date1 || !date2) return false;
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) return false;

  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear() &&
    date1.getHours() === date2.getHours() &&
    date1.getMinutes() === date2.getMinutes()
  );
}

export function dateBefore(date1: Date, date2: Date, withoutHours: boolean = true) {
  if (withoutHours) {
    date1.setHours(0, 0, 0, 0);
    date2.setHours(0, 0, 0, 0);
  }
  return date1.getTime() < date2.getTime();
}

export function getWeekStartWeekEndDates(date: Date): Date[] {
  //1er jour de la semaine actuelle
  const startDate = date;
  startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
  startDate.setHours(0, 0, 0, 0);

  //dernier jour de la semaine actuelle
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);

  return [startDate, endDate];
}

export function getNext7DaysDates(date: Date): Date[] {
  let startDate = new Date(date);

  let endDate = new Date(date);
  endDate.setDate(endDate.getDate() + 7);
  return getDatesBetween(startDate, endDate);
}

export function getDatesBetween(start: Date, end: Date): Date[] {
  const dates: Date[] = [];

  const current = new Date(start);
  current.setHours(0, 0, 0, 0); // Normalise à minuit
  const endDate = new Date(end);
  endDate.setHours(0, 0, 0, 0);

  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

export function cleanJson(input: string): any {
  return input
    .replace(/[\r\n]+/g, ' ') // Remplacer les retours à la ligne
    .replace(/\s+/g, ' ') // Normaliser les espaces
    .trim();
}

export async function copyToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    return false;
  }
}
